/**
 * Atmosphere A2 Slice B (2026-06-03) — Roll-cinematic state machine.
 *
 * Tracks which trigger path initiated the current tumble (selector
 * button vs voice transcript) and the player's voice intent text for
 * the transcript bubble overlay.
 *
 * The store is the single source of truth for the cinematic mode so
 * any subscriber (DiceRollTumble, voice-confirmation listener, future
 * A3 QR cinematic response) can read the active trigger path without
 * prop drilling.
 *
 * Lifecycle:
 *   - `startSelector()`  — Roll d100 button clicked
 *   - `startVoice(text)` — dispatcher fired roll_skill from a voice transcript
 *   - `end()`            — DiceRollTumble onAnimationComplete fired
 */
import { create } from 'zustand';

export type RollCinematicMode = 'idle' | 'selector' | 'voice';

export interface RollCinematicState {
  mode: RollCinematicMode;
  /** Player utterance that triggered the voice-mode roll (rendered in the
   *  transcript bubble). null in selector mode. */
  voiceIntent: string | null;
  startSelector: () => void;
  startVoice: (intent: string | null) => void;
  end: () => void;
}

export const useRollCinematicStore = create<RollCinematicState>((set) => ({
  mode: 'idle',
  voiceIntent: null,
  startSelector: () => set({ mode: 'selector', voiceIntent: null }),
  startVoice: (intent) => set({ mode: 'voice', voiceIntent: intent }),
  end: () => set({ mode: 'idle', voiceIntent: null }),
}));

/**
 * Reset helper for tests. Restores the store to initial idle state.
 */
export function resetRollCinematicStoreForTests(): void {
  useRollCinematicStore.setState({ mode: 'idle', voiceIntent: null });
}
